import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    CCard, CCardBody, CCardHeader, CBadge, CButton, CButtonGroup,
    CDropdown, CDropdownToggle, CDropdownMenu, CFormCheck
} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import { cilList } from "@coreui/icons";

import { apiClient } from 'services/Api';
import AlertNotification from 'components/globals/AlertNotification';
import DataTableComponent from 'components/DataTableComponent';
import { usePersistedState } from 'components/hooks/usePersistedState';

const STATUS_SORT_ORDER = {
    'Desenvolvimento': 0,
    'Stand-by': 1,
    'Desistido/Integrado': 2,
    'Fechado': 3,
};

const STATUS_FILTERS = [
    ['all', 'Todos'],
    ['Desenvolvimento', 'Desenvolvimento'],
    ['Stand-by', 'Stand-by'],
    ['Desistido/Integrado', 'Desistido/Integrado'],
    ['Fechado', 'Fechado'],
];

// Paleta moderna e limpa, cor por categoria bem distinta.
const GROUP_COLORS = {
    estado: { header: "#60A5FA", body: "#DBEAFE" },        // azul
    projeto: { header: "#FBBF24", body: "#FEF3C7" },       // âmbar
    elaboracao: { header: "#34D399", body: "#D1FAE5" },    // esmeralda
    financiamento: { header: "#A78BFA", body: "#EDE9FE" }, // violeta
    acompanhamento: { header: "#FB7185", body: "#FFE4E6" }, // rosa
    avaliacao: { header: "#94A3B8", body: "#F1F5F9" },     // cinza-azulado
};

// Ordem e nomes das categorias, tal como na tabela antiga.
const GROUP_ORDER = [
    ["estado", "Estado"],
    ["projeto", "Projeto"],
    ["elaboracao", "Elab. Inicial"],
    ["financiamento", "Financiamento"],
    ["acompanhamento", "Acompanh."],
    ["avaliacao", "Avaliação"],
];

const DEFAULT_VISIBLE_GROUPS = ["estado", "projeto"];

const sortIdiProjectsByStatus = (projects) =>
    projects
        .map((project, index) => ({ project, index }))
        .sort((a, b) => {
            const aOrder = STATUS_SORT_ORDER[a.project.status] ?? Number.MAX_SAFE_INTEGER;
            const bOrder = STATUS_SORT_ORDER[b.project.status] ?? Number.MAX_SAFE_INTEGER;

            if (aOrder !== bOrder) {
                return aOrder - bOrder;
            }

            return a.index - b.index;
        })
        .map(({ project }) => project);

const getInnovationType = (project) => {
    const types = [];

    if (project.organizational) types.push("Organizacional");
    if (project.service) types.push("Serviço");
    if (project.marketing) types.push("Marketing");
    if (project.process) types.push("Processo");
    if (project.product) types.push("Produto");

    return types.length > 0 ? types.join(", ") : "-";
};

const formatCell = (value, type) => {
    if (value === null || value === undefined || value === '') return '-';
    switch (type) {
        case 'currency':
            return new Intl.NumberFormat('pt-PT', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0,
            }).format(Number(value));
        case 'percentage':
            return `${Number(value).toFixed(1)}%`;
        case 'integer':
            return new Intl.NumberFormat('pt-PT').format(Number(value));
        case 'badge': {
            const badgeColor =
                value === 'Stand-by' || value === 'Desistido/Integrado'
                    ? 'danger'
                    : value === 'Fechado'
                        ? 'info'
                        : value === 'Desenvolvimento'
                            ? 'success'
                            : '';
            return <CBadge color={badgeColor}>{value}</CBadge>;
        }
        default:
            return value;
    }
};

// Mesma organização de campos que a tabela antiga tinha (por categoria), só
// que agora cada um vira uma coluna própria (esconde/mostra em bloco) em vez
// de uma célula numa grelha com cabeçalho agrupado.
const FIELD_DEFS = [
    { key: "status", label: "Estado", type: "badge", minWidth: "140px", group: "estado" },
    { key: "code", label: "Código", type: "text", minWidth: "120px", group: "projeto" },
    { key: "project_type", label: "Tipo", type: "text", minWidth: "100px", group: "projeto" },
    { key: "innovation_type", label: "Natureza", type: "text", minWidth: "140px", group: "projeto" },
    { key: "name", label: "Descrição", type: "text", grow: 2, minWidth: "160px", group: "projeto" },
    { key: "manager_name", label: "Gestor", type: "text", minWidth: "130px", group: "projeto" },
    { key: "start_date", label: "Data Início", type: "text", minWidth: "170px", group: "elaboracao" },
    { key: "planned_duration", label: "Duração Prevista", type: "text", minWidth: "120px", group: "elaboracao" },
    { key: "evaluation_months_after", label: "Meses até Avaliação", type: "text", minWidth: "150px", group: "elaboracao" },
    { key: "milestones_defined", label: "Milestones Previstos", type: "text", minWidth: "140px", group: "elaboracao" },
    { key: "objectives_defined", label: "Objetivos Previstos", type: "text", minWidth: "140px", group: "elaboracao" },
    { key: "budget", label: "Orçamento", type: "text", minWidth: "110px", group: "elaboracao" },
    { key: "financing", label: "Financiamento", type: "text", minWidth: "150px", group: "financiamento" },
    { key: "time_deviation_months", label: "Desvio Temporal", type: "text", minWidth: "150px", group: "acompanhamento" },
    { key: "progress", label: "Concretizado (%)", type: "text", minWidth: "120px", group: "acompanhamento" },
    { key: "end_date", label: "Data de Fecho", type: "text", minWidth: "130px", group: "avaliacao" },
    { key: "milestones_completed", label: "Milestones Cumpridos", type: "text", minWidth: "150px", group: "avaliacao" },
    { key: "milestone_completion_pct", label: "% Milestones Cumpridos", type: "text", minWidth: "150px", group: "avaliacao" },
    { key: "objectives_completed", label: "Objetivos Cumpridos", type: "text", minWidth: "150px", group: "avaliacao" },
    { key: "objective_compliance", label: "% Cumprimento Objetivos", type: "text", minWidth: "160px", group: "avaliacao" },
    { key: "actual_duration", label: "Duração", type: "text", minWidth: "100px", group: "avaliacao" },
    { key: "time_deviation_pct", label: "Desvio Temporal (%)", type: "text", minWidth: "140px", group: "avaliacao" },
    { key: "evaluation_date", label: "Data Avaliação", type: "text", minWidth: "120px", group: "avaliacao" },
    { key: "evaluation_score", label: "Pontuação", type: "text", minWidth: "110px", group: "avaliacao" },
    { key: "evaluation_class", label: "Classificação", type: "text", minWidth: "120px", group: "avaliacao" },
];

const IdiProjectList = () => {
    const [idiProjects, setIdiProjects] = useState([]);
    const location = useLocation();
    const [filterType, setFilterType] = useState("all");
    const [visibleGroups, setVisibleGroups] = usePersistedState(
        "idi-projects:visible-groups",
        DEFAULT_VISIBLE_GROUPS,
    );

    useEffect(() => {
        const fetchIdiProjects = async () => {
            try {
                const response = await apiClient.get(`/idi-projects/`);
                setIdiProjects(
                    sortIdiProjectsByStatus(response.data).map((project) => ({
                        ...project,
                        innovation_type: getInnovationType(project),
                    }))
                );
            } catch (error) {
                console.error('Error fetching action:', error);
            }
        };

        fetchIdiProjects();
    }, [location.pathname]);

    const filteredProjects = filterType === "all"
        ? idiProjects
        : idiProjects.filter((project) => project.status === filterType);

    const toggleGroup = (groupKey) => {
        setVisibleGroups((current) =>
            current.includes(groupKey)
                ? current.filter((g) => g !== groupKey)
                : [...current, groupKey]
        );
    };

    const visibleFieldDefs = FIELD_DEFS.filter((def) => visibleGroups.includes(def.group));

    // Primeira coluna de cada grupo visível — é a única que recebe a
    // etiqueta da categoria, para não se repetir nem ficar parecida com o
    // nome de uma coluna normal. (Usar a do meio ficava estranho quando um
    // grupo tem um número ímpar de colunas.) Grupos com uma só coluna não
    // mostram etiqueta — seria redundante repetir o mesmo nome duas vezes.
    const groupSizes = {};
    visibleFieldDefs.forEach((def) => {
        groupSizes[def.group] = (groupSizes[def.group] || 0) + 1;
    });
    const firstIndexByGroup = {};
    visibleFieldDefs.forEach((def, index) => {
        if (!(def.group in firstIndexByGroup)) firstIndexByGroup[def.group] = index;
    });

    const columns = visibleFieldDefs.map((def, index) => {
        const isGroupFirst = firstIndexByGroup[def.group] === index && groupSizes[def.group] > 1;
        const groupLabel = GROUP_ORDER.find(([key]) => key === def.group)?.[1];

        return {
            id: def.key,
            name: isGroupFirst ? (
                <div style={{ position: "relative", width: "100%", minHeight: "42px" }}>
                    <span
                        className="text-uppercase fw-bold"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "rgba(20,20,20,0.75)",
                            color: "#fff",
                            padding: "1px 10px",
                            borderRadius: "999px",
                            fontSize: "0.6rem",
                            letterSpacing: "0.04em",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {groupLabel}
                    </span>
                    <span
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {def.label}
                    </span>
                </div>
            ) : def.label,
            selector: (row) => row[def.key],
            cell: (row) => formatCell(row[def.key], def.type),
            sortable: true,
            sortFunction: def.key === "status"
                ? (a, b) => (STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99)
                : undefined,
            wrap: true,
            grow: def.grow,
            minWidth: def.minWidth,
            style: { backgroundColor: GROUP_COLORS[def.group].body },
        };
    });

    const headerColorRules = visibleFieldDefs
        .map((def) => `.idi-projects-table .rdt_TableCol[data-column-id="${def.key}"] { background-color: ${GROUP_COLORS[def.group].header} !important; }`)
        .join("\n");

    return (
        <div className="container-fluid mt-4">
            <AlertNotification />
            <style>{headerColorRules}</style>
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3 className="mb-0">Projetos IDI</h3>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-medium-emphasis small fw-semibold">Estado:</span>
                        <CButtonGroup role="group" size="sm" className="gap-1">
                            {STATUS_FILTERS.map(([value, label]) => (
                                <CButton
                                    key={value}
                                    variant={filterType !== value ? "outline" : null}
                                    color={filterType === value ? "primary" : "secondary"}
                                    onClick={() => setFilterType(value)}
                                >
                                    {label}
                                </CButton>
                            ))}
                        </CButtonGroup>
                        <CDropdown alignment="end" autoClose="outside">
                            <CDropdownToggle color="secondary" variant="outline" size="sm">
                                <CIcon icon={cilList} className="me-1" />
                                Colunas
                            </CDropdownToggle>
                            <CDropdownMenu style={{ minWidth: "220px" }} className="px-3 py-2">
                                {GROUP_ORDER.map(([groupKey, groupLabel]) => (
                                    <CFormCheck
                                        key={groupKey}
                                        id={`group-${groupKey}`}
                                        label={groupLabel}
                                        checked={visibleGroups.includes(groupKey)}
                                        onChange={() => toggleGroup(groupKey)}
                                    />
                                ))}
                            </CDropdownMenu>
                        </CDropdown>
                    </div>
                </CCardHeader>
                <CCardBody>
                    <div className="idi-projects-table">
                        <DataTableComponent
                            columns={columns}
                            data={filteredProjects}
                            defaultSortFieldId="status"
                            persistKey="idi-projects-table"
                            pagination={false}
                        />
                    </div>
                </CCardBody>
            </CCard>
        </div>
    );
};

export default IdiProjectList;
